export type BlockedStorageApi = 'localStorage' | 'indexedDB' | 'sessionStorage'

export interface StorageViolation {
  api: BlockedStorageApi
  line: number
  column: number
  lineContent: string
  file: string
}

export interface StorageLinterOptions {
  checkSessionStorage?: boolean
  ignoredApis?: BlockedStorageApi[]
}

const IGNORE_DIRECTIVE_PATTERN = /(?:@rundot-ignore-storage|rundot-ignore-storage)/i

type ScannerState =
  | { type: 'code' }
  | { type: 'single-quote' }
  | { type: 'double-quote' }
  | { type: 'line-comment'; startLine: number; buffer: string }
  | { type: 'block-comment'; startLine: number; buffer: string }
  | { type: 'template' }
  | { type: 'template-expr'; braceDepth: number }

/**
 * Strip strings and comments while preserving line breaks and character positions,
 * making lexical analysis safe against false positives in string literals and comments.
 * Handles nested template literal expressions (`${...}`) and only extracts ignore
 * directives from actual comments.
 */
function stripStringsAndComments(source: string): { clean: string; ignoreLines: Set<number> } {
  const ignoreLines = new Set<number>()
  const stateStack: ScannerState[] = [{ type: 'code' }]

  let result = ''
  let i = 0
  let currentLine = 1
  const len = source.length

  while (i < len) {
    const ch = source[i]
    const next = source[i + 1]
    const current = stateStack[stateStack.length - 1]

    if (current.type === 'line-comment') {
      if (ch === '\n') {
        if (IGNORE_DIRECTIVE_PATTERN.test(current.buffer)) {
          ignoreLines.add(current.startLine)
          ignoreLines.add(current.startLine + 1)
        }
        stateStack.pop()
        result += '\n'
        currentLine++
      } else {
        current.buffer += ch
        result += ' '
      }
      i++
      continue
    }

    if (current.type === 'block-comment') {
      if (ch === '*' && next === '/') {
        if (IGNORE_DIRECTIVE_PATTERN.test(current.buffer)) {
          ignoreLines.add(current.startLine)
          for (let l = current.startLine; l <= currentLine + 1; l++) {
            ignoreLines.add(l)
          }
        }
        stateStack.pop()
        result += '  '
        i += 2
      } else if (ch === '\n') {
        result += '\n'
        currentLine++
        i++
      } else {
        current.buffer += ch
        result += ' '
        i++
      }
      continue
    }

    if (current.type === 'single-quote') {
      if (ch === '\\') {
        result += '  '
        i += 2
      } else if (ch === "'") {
        stateStack.pop()
        result += ' '
        i++
      } else if (ch === '\n') {
        // Unescaped newline resets string state (JS strings cannot span unescaped newlines)
        stateStack.pop()
        result += '\n'
        currentLine++
        i++
      } else {
        result += ' '
        i++
      }
      continue
    }

    if (current.type === 'double-quote') {
      if (ch === '\\') {
        result += '  '
        i += 2
      } else if (ch === '"') {
        stateStack.pop()
        result += ' '
        i++
      } else if (ch === '\n') {
        // Unescaped newline resets string state
        stateStack.pop()
        result += '\n'
        currentLine++
        i++
      } else {
        result += ' '
        i++
      }
      continue
    }

    if (current.type === 'template') {
      if (ch === '\\') {
        result += '  '
        i += 2
      } else if (ch === '`') {
        stateStack.pop()
        result += ' '
        i++
      } else if (ch === '$' && next === '{') {
        stateStack.push({ type: 'template-expr', braceDepth: 1 })
        result += '  '
        i += 2
      } else if (ch === '\n') {
        result += '\n'
        currentLine++
        i++
      } else {
        result += ' '
        i++
      }
      continue
    }

    // current is 'code' or 'template-expr'
    if (current.type === 'template-expr') {
      if (ch === '{') {
        current.braceDepth++
        result += '{'
        i++
        continue
      } else if (ch === '}') {
        current.braceDepth--
        if (current.braceDepth === 0) {
          stateStack.pop() // back to 'template'
          result += ' '
          i++
          continue
        } else {
          result += '}'
          i++
          continue
        }
      }
    }

    if (ch === '/' && next === '/') {
      stateStack.push({ type: 'line-comment', startLine: currentLine, buffer: '' })
      result += '  '
      i += 2
      continue
    }

    if (ch === '/' && next === '*') {
      stateStack.push({ type: 'block-comment', startLine: currentLine, buffer: '' })
      result += '  '
      i += 2
      continue
    }

    if (ch === "'") {
      // Don't treat word contractions in JSX text (e.g. Don't, It's, let's) as string delimiters
      if (i > 0 && /[\w$]/.test(source[i - 1]) && i + 1 < len && /[\w$]/.test(source[i + 1])) {
        result += ' '
        i++
        continue
      }
      stateStack.push({ type: 'single-quote' })
      result += ' '
      i++
      continue
    }

    if (ch === '"') {
      stateStack.push({ type: 'double-quote' })
      result += ' '
      i++
      continue
    }

    if (ch === '`') {
      stateStack.push({ type: 'template' })
      result += ' '
      i++
      continue
    }

    if (ch === '\n') {
      result += '\n'
      currentLine++
      i++
      continue
    }

    result += ch
    i++
  }

  // Handle trailing line comment if file ended without newline
  const lastState = stateStack[stateStack.length - 1]
  if (lastState.type === 'line-comment' && IGNORE_DIRECTIVE_PATTERN.test(lastState.buffer)) {
    ignoreLines.add(lastState.startLine)
    ignoreLines.add(lastState.startLine + 1)
  }

  return { clean: result, ignoreLines }
}

const GLOBAL_PREFIX_PATTERN = /^(?:window|globalThis|self|top|parent)\s*(?:\?\.)/

/**
 * Scan source code for prohibited browser storage API calls.
 */
export function scanForBlockedStorage(
  code: string,
  filePath: string,
  options: StorageLinterOptions = {},
): StorageViolation[] {
  const { clean, ignoreLines } = stripStringsAndComments(code)
  const violations: StorageViolation[] = []
  const rawLines = code.split('\n')

  const targetApis: BlockedStorageApi[] = (
    ['localStorage', 'indexedDB', ...(options.checkSessionStorage !== false ? ['sessionStorage'] : [])] as BlockedStorageApi[]
  ).filter((api) => !options.ignoredApis?.includes(api))

  for (const api of targetApis) {
    // Matches global prefix or standalone identifier
    const pattern = new RegExp(
      `(?<![\\w$])(?:(?:window|globalThis|self|top|parent)\\s*(?:\\?\\.|\\.)\\s*)?(${api})(?![\\w$])`,
      'g',
    )
    let match: RegExpExecArray | null

    while ((match = pattern.exec(clean)) !== null) {
      const matchIndex = match.index
      const fullMatch = match[0]

      // Check if preceded by '.' or '?.' from a non-global object (e.g. myObj.localStorage or myObj?.localStorage)
      const isExplicitGlobal =
        fullMatch.startsWith('window') ||
        fullMatch.startsWith('globalThis') ||
        fullMatch.startsWith('self') ||
        fullMatch.startsWith('top') ||
        fullMatch.startsWith('parent')

      if (!isExplicitGlobal && matchIndex > 0) {
        // Look at preceding non-whitespace character
        let prevIdx = matchIndex - 1
        while (prevIdx >= 0 && /\s/.test(clean[prevIdx])) {
          prevIdx--
        }
        if (prevIdx >= 0 && clean[prevIdx] === '.') {
          // Preceded by '.' or '?.' from a custom object, skip
          continue
        }
      }

      // Check if this is an object property key definition or TypeScript type member
      if (!isExplicitGlobal) {
        let afterIdx = matchIndex + fullMatch.length
        while (afterIdx < clean.length && /\s/.test(clean[afterIdx])) {
          afterIdx++
        }
        if (clean[afterIdx] === '?' && clean[afterIdx + 1] === ':') {
          // Type member like `localStorage?: boolean`
          continue
        }
        if (clean[afterIdx] === ':') {
          // 1. Check if we are in a ternary expression (e.g. `cond ? localStorage : fallback`)
          let hasTernaryQuestion = false
          let backIdx = matchIndex - 1
          while (backIdx >= 0 && clean[backIdx] !== ';') {
            if (clean[backIdx] === '?') {
              hasTernaryQuestion = true
              break
            }
            backIdx--
          }

          if (!hasTernaryQuestion) {
            // 2. Check if this is a destructuring alias from global object, e.g. `const { localStorage: myStorage } = window`
            let isDestructuringFromGlobal = false
            const closeBraceIdx = clean.indexOf('}', afterIdx)
            if (closeBraceIdx !== -1) {
              const afterBrace = clean.slice(closeBraceIdx, closeBraceIdx + 100)
              const destructureRhs = afterBrace.match(/^\}\s*=\s*([a-zA-Z0-9_$]+(?:\.[a-zA-Z0-9_$]+)*)/)
              if (destructureRhs) {
                const rhsTarget = destructureRhs[1]
                if (/^(?:window|globalThis|self|top|parent)\b/.test(rhsTarget)) {
                  isDestructuringFromGlobal = true
                }
              }
            }

            if (!isDestructuringFromGlobal) {
              // Plain object literal property or interface/type definition
              continue
            }
          }
        }
      }

      const apiIndex = matchIndex + (fullMatch.length - api.length)

      // Calculate line and column (1-indexed)
      const textBefore = clean.slice(0, apiIndex)
      const lineBreaks = textBefore.split('\n')
      const lineNumber = lineBreaks.length
      const columnNumber = lineBreaks[lineBreaks.length - 1].length + 1

      if (ignoreLines.has(lineNumber)) {
        continue
      }

      violations.push({
        api,
        line: lineNumber,
        column: columnNumber,
        lineContent: rawLines[lineNumber - 1] || '',
        file: filePath,
      })
    }
  }

  return violations
}

/**
 * Format a human-friendly, high-visibility error message with remediation guidance.
 */
export function formatStorageViolationError(
  violation: StorageViolation,
  sourceCode: string,
  filePath: string,
): string {
  const lines = sourceCode.split('\n')
  const { line, column, api } = violation
  const lineContent = lines[line - 1] || ''

  const pointerPadding = ' '.repeat(Math.max(0, column - 1))
  const pointerUnderline = '^'.repeat(api.length)

  const lineNumStr = String(line).padStart(4, ' ')

  return [
    `🚨 [RUN SDK] Blocked Browser Storage Detected in ${filePath}:${line}:${column}`,
    '',
    `  ${lineNumStr} | ${lineContent}`,
    `       | ${pointerPadding}${pointerUnderline}`,
    '',
    `  ⚠️  WHY THIS FAILS:`,
    `      Direct access to '${api}' is BLOCKED in production H5 games.`,
    `      Games run in an opaque sandbox ("null" origin) where browser storage`,
    `      throws a DOMException (SecurityError).`,
    '',
    `  💡 RECOMMENDED FIX:`,
    `      • For local device caching (same device):`,
    `          await RundotGameAPI.deviceCache.setItem('key', value);`,
    `          const val = await RundotGameAPI.deviceCache.getItem('key');`,
    `      • For persistent cloud player save (synced across devices):`,
    `          await RundotGameAPI.appStorage.setItem('key', value);`,
    '',
    `  🚫 TO BYPASS THIS CHECK:`,
    `      • Inline: add '// @rundot-ignore-storage' on this line`,
    `      • In vite.config.ts: rundotGameLibrariesPlugin({ storageCheck: false })`,
    `      • Via env var: RUNDOT_DISABLE_STORAGE_CHECK=true`,
  ].join('\n')
}
